import React, { useState } from 'react';
import { Printer, Activity } from 'lucide-react';
import Banner from '../../components/layout/Banner';
import PageTitle from '../../components/layout/PageTitle';
import JourneyToolbar from '../../components/features/Journey/JourneyToolbar';
import GoalFilter from '../../components/features/Journey/GoalFilter';
import AiSummary from '../../components/features/Journey/AiSummary';
import SeverityLegend from '../../components/features/Journey/SeverityLegend';
import WeekGrid from '../../components/features/Journey/WeekGrid';
import MonthGrid from '../../components/features/Journey/MonthGrid';
import YearGrid from '../../components/features/Journey/YearGrid';
import DayTimeline from '../../components/features/Journey/DayTimeline';
import ReportEntries from '../../components/features/Journey/ReportEntries';
import ReportRangePicker from '../../components/features/Journey/ReportRangePicker';
import IndicatorTable from '../../components/features/Journey/IndicatorTable';
import useJourney from '../../hooks/useJourney';
import { useMockUser } from '../../context/MockUserContext';
import { appFeatures } from '../../constants/features';
import { shiftAnchor, todayISO, parseISODate } from '../../utils/journeyDates';
import styles from './JourneyPage.module.css';

// The goal filter narrows the calendar cells. The day view ignores it (its
// timeline is the whole day across every goal) and the report has no goal
// parameter at all, so the control only appears where it does something.
const VIEWS_WITH_GOAL_FILTER = ['week', 'month', 'year'];

// The grid views draw one face per slot, so several ratings — whether they are
// different goals or the same goal logged twice — collapse into one. The day
// timeline and the report list everything individually and need no such note.
const VIEWS_THAT_COMBINE_RATINGS = ['week', 'month', 'year'];

// The design names the summary after the period it covers: "WEEK SUMMARY".
const SUMMARY_TITLES = {
	day: 'Day summary',
	week: 'Week summary',
	month: 'Month summary',
	year: 'Year summary',
	report: 'Report summary',
};

const JourneyPage = ({ onLogoClick, goals = [] }) => {
	const { users, currentUserId } = useMockUser();
	const journeyFeature = appFeatures.find((feature) => feature.id === 'journey');

	const [view, setView] = useState('week');
	const [anchorDate, setAnchorDate] = useState(todayISO);
	const [goalSettingId, setGoalSettingId] = useState(null);
	const [reportPeriod, setReportPeriod] = useState('last_30_days');
	const [reportStart, setReportStart] = useState('');
	const [reportEnd, setReportEnd] = useState('');

	const {
		period,
		ai,
		cells,
		entries,
		indicators,
		loading,
		refreshing,
		error,
		awaitingCustomRange,
	} = useJourney({
		view,
		date: anchorDate,
		goalSettingId: VIEWS_WITH_GOAL_FILTER.includes(view) ? goalSettingId : null,
		reportPeriod,
		reportStart,
		reportEnd,
		userId: currentUserId,
	});

	const isReport = view === 'report';
	// Printed reports leave the app, so they have to say who they are about and
	// when they were produced — the screen never needs either.
	const patientName =
		users.find((user) => String(user.id) === String(currentUserId))?.name ?? '';
	const printedOn = new Date().toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	});
	const showGoalFilter = VIEWS_WITH_GOAL_FILTER.includes(view);
	// The year view shows no indicator table, matching the design.
	const showIndicators = view !== 'year' && indicators.length > 0;

	// The AI text is written per period ahead of time, so a period can hold a
	// fortnight of tracking and still have nothing written about it. Saying
	// which of the two it is keeps an ungenerated summary from reading as data
	// that went missing.
	const aiEmptyMessage =
		cells.length > 0 || entries.length > 0
			? 'No summary has been written for this period yet.'
			: 'Nothing tracked in this period yet.';

	// Chrome names the saved PDF after `document.title`, which is otherwise the
	// app's own title ("eCare PD") and tells the person filing it nothing. It is
	// set for the duration of the print dialog and put back afterwards.
	const handlePrint = () => {
		const previousTitle = document.title;
		document.title = [
			'Journey report',
			patientName,
			period ? `${period.start} to ${period.end}` : null,
		]
			.filter(Boolean)
			.join(' - ');

		const restore = () => {
			document.title = previousTitle;
			window.removeEventListener('afterprint', restore);
		};
		window.addEventListener('afterprint', restore);
		window.print();
	};

	const openDay = (date) => {
		setAnchorDate(date);
		setView('day');
	};

	const openMonth = (date) => {
		setAnchorDate(date);
		setView('month');
	};

	const renderVisual = () => {
		if (!period) return null;

		if (isReport) return <ReportEntries entries={entries} />;
		if (view === 'day') return <DayTimeline entries={entries} period={period} />;
		if (view === 'week') return <WeekGrid period={period} cells={cells} />;
		if (view === 'year') {
			return <YearGrid period={period} cells={cells} onSelectMonth={openMonth} />;
		}

		const monthStart = parseISODate(period.start);
		const ratingsByDate = cells.reduce((map, cell) => {
			map[cell.date] = cell.rating;
			return map;
		}, {});

		return (
			<MonthGrid
				year={monthStart.getFullYear()}
				monthIndex={monthStart.getMonth()}
				ratingsByDate={ratingsByDate}
				cellSize={30}
				onSelectDate={openDay}
			/>
		);
	};

	return (
		<div className={styles.container}>
			<Banner onLogoClick={onLogoClick} />
			<div className={styles.content}>
				{/* The icon badge prints as an empty circle — its glyph is white —
				    and the letterhead below already names the report. */}
				<div className={styles.pageTitle}>
					<PageTitle
						icon={journeyFeature.icon}
						title={journeyFeature.title}
						color={journeyFeature.color}
					/>
				</div>

				<JourneyToolbar
					view={view}
					onViewChange={setView}
					onPrev={() => setAnchorDate((date) => shiftAnchor(date, view, -1))}
					onNext={() => setAnchorDate((date) => shiftAnchor(date, view, 1))}
					onToday={() => setAnchorDate(todayISO())}
				/>

				{isReport && (
					<ReportRangePicker
						period={reportPeriod}
						start={reportStart}
						end={reportEnd}
						onPeriodChange={setReportPeriod}
						onStartChange={setReportStart}
						onEndChange={setReportEnd}
					/>
				)}

				{/* Only ever seen on paper: a letterhead for whoever the report is
				    handed to. */}
				<div className={styles.printHeader}>
					{patientName && (
						<span className={styles.printPatient}>{patientName}</span>
					)}
					<span>Journey report{period ? ` — ${period.label}` : ''}</span>
					<span>Printed {printedOn}</span>
				</div>

				<div className={styles.periodBar}>
					<div className={styles.periodHeading}>
						{/* The period already on screen stays up while the next one
						    loads, so a refresh changes this note and nothing else. */}
						<h2 className={styles.periodTitle}>{period?.label ?? ''}</h2>
						{refreshing && (
							<span className={styles.refreshing} role='status'>
								Updating…
							</span>
						)}
					</div>
					<div className={styles.periodActions}>
						{isReport && (
							<button
								type='button'
								className={styles.printButton}
								onClick={handlePrint}
							>
								<Printer size={16} />
								Print
							</button>
						)}
					</div>
				</div>

				{awaitingCustomRange && (
					<p className={styles.notice}>
						Pick a start and an end date to build the report.
					</p>
				)}

				{loading && <p className={styles.notice}>Loading your journey…</p>}

				{error && <p className={styles.errorNotice}>{error}</p>}

				{!loading && !error && !awaitingCustomRange && period && (
					<>
						<AiSummary
							title={SUMMARY_TITLES[view]}
							summary={ai.summary}
							insights={ai.insights}
							emptyMessage={aiEmptyMessage}
						/>

						<section className={styles.section}>
							<div className={styles.sectionHeader}>
								{/* The filter heads the section in place of a title: it names
								    what is on screen ("Goals Overview", or one goal) and it
								    narrows these cells and nothing else — not the summary
								    above, not the indicator table below. */}
								{isReport ? (
									<h3 className={styles.sectionTitle}>Everything you tracked</h3>
								) : (
									showGoalFilter && (
										<GoalFilter
											goals={goals}
											value={goalSettingId}
											onChange={setGoalSettingId}
										/>
									)
								)}
								{!isReport && <SeverityLegend />}
							</div>
							{renderVisual()}
							{VIEWS_THAT_COMBINE_RATINGS.includes(view) &&
								cells.length > 0 && (
									<p className={styles.gridNote}>
										Where more than one rating falls in the same block of time,
										the face shows the one you found hardest.
									</p>
								)}
						</section>

						{showIndicators && (
							<section className={styles.section}>
								<h3 className={styles.sectionTitle}>
									<Activity className={styles.sectionIcon} />
									Automated tracking
								</h3>
								<IndicatorTable indicators={indicators} />
							</section>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default JourneyPage;
